import { produce } from 'immer';
import { cloneDeep } from 'lodash';
import {
  Project,
  Baseline,
  IElement,
  OneOfElements,
  IElementID,
  OneOfParentElements,
} from '@shared/models/project.model';
import {
  ElementPath,
  getChildElements,
  getAllBuildingVersions,
  getElementById,
  flattenElements,
} from '../recursive-element.helpers';
import { projectGuards } from '../guards/project-guards.helpers';
import { hasPartialChanges, omit } from '../object.helpers';
import { cacheByArguments } from '../function.helpers';
import { RequireProperties } from '@shared/models/type-helpers.model';
import { profiler } from './pipeline-profiler';
import {
  generateElementFallbackName,
  getFallbackName,
  getVersionById,
  removeElements,
  updateBuildingVersionProperties,
} from '../project.helpers';
import { isElement } from '../recursive-element.helpers';
import type { RecipeRecord } from '@shared/models/recipe.model';
import { isOneOf } from '../array.helpers';
import {
  ProjectChange,
  ProjectChangeMap,
  ProjectChangeType,
  VersionChange,
  versionChangeTypes,
} from './update-engine.models';
import {
  applyElementAddChange,
  applyVersionAddChange,
  applyVersionMutateChange,
  applyVersionRemoveChange,
  versionMutateChangeTypes,
  type ElementAddChange,
  type VersionAddChange,
  type VersionMutateChange,
  type VersionRemoveChange,
} from './mutators';
import {
  cleanupActionsForRemovedElementsInProject,
  collectRemovedElementIdsByVersion,
  dropRedundantChangesFromStoredProposalActionsInProject,
  stripProjectChangesForRemovedElements,
} from '../actions/clean-actions.helpers';
import { projectChangeToElementPartial, mergeElementPartials } from './updates';
import { versionChangeToPartial } from './updates/version-change.update';
import { reportSharedError } from '../error-reporting.helpers';
import { isRecipeScopedElementId } from '../recipe.helpers';

/**
 * Path representation for navigating the project tree
 * Each number represents an index in the elements array at that level
 */
export type ElementProjectPath = {
  buildingIndex: number;
  versionIndex: number;
  elementIndices: number[];
};

/**
 * Index mapping element IDs to their paths in the project tree
 */
export type ElementProjectPathMap = Map<IElementID, ElementProjectPath>;

/**
 * A change to apply to an element
 */
/** Partial element patch applied in batch updates. */
export type ElementPartial = Partial<OneOfElements>;

/**
 * A change with required id to apply to an element
 */
export type ElementIdChange = RequireProperties<ElementPartial, 'id'>;

export type ElementChangeMap = Map<IElementID, ElementPartial>;

/** {@link ProjectChange} payload removing an element subtree. */
export type ElementRemoveChange =
  ProjectChangeMap[ProjectChangeType.ElementRemove];

/** Per-version, per-kind bucket of structural mutator changes. */
export type ScopedMutatorBucket = {
  add: Map<string, VersionMutateChange[]>;
  remove: Map<string, VersionMutateChange[]>;
  update: Map<string, VersionMutateChange[]>;
};

/**
 * Structured set of changes the unified {@link applyBatchChanges} pipeline understands.
 *
 * Layout mirrors the `(scope, kind)` metadata declared on each handler entry in
 * `pipeline/updates/index.ts`:
 *
 * - `element.add` / `element.remove` — project-level structural element ops applied via
 *   {@link applyElementAddChange} / {@link removeElements} (+ action cleanup).
 * - `element.update` — element-id grouped raw changes; partials resolved against the
 *   post-structural project so handlers see freshly added elements.
 * - `version.add` / `version.remove` — whole building versions applied via
 *   {@link applyVersionAddChange} / {@link applyVersionRemoveChange}.
 * - `version.update` — version-id grouped raw changes mixing primitive partials
 *   (`VersionName/Products/Results`, dispatched via {@link versionChangeToPartial}) and
 *   bulk resets (`VersionClearProposals`, dispatched via {@link applyVersionMutateChange}).
 * - `proposal.{add,remove,update}` / `action.{add,remove,update}` — per-version chains of
 *   structural mutators. Each returns a full updated {@link Baseline} reference so
 *   concurrent edits to the same collection compose without clobbering each other.
 *
 * Application order is fixed across scopes (see {@link applyBatchChanges}) so structural
 * project-level changes precede per-version mutators precede partials. Within a single
 * `(scope, kind)` bucket per version, changes apply in insertion order.
 */
export type BatchChangeMap = {
  element: {
    add: ElementAddChange[];
    remove: ElementRemoveChange[];
    update: Map<string, ProjectChange[]>;
  };
  version: {
    add: VersionAddChange[];
    remove: VersionRemoveChange[];
    update: Map<string, ProjectChange[]>;
  };
  proposal: ScopedMutatorBucket;
  action: ScopedMutatorBucket;
};

/** Empty `BatchChangeMap` with all buckets initialised. */
export const createEmptyBatchChangeMap = (): BatchChangeMap => ({
  element: {
    add: [],
    remove: [],
    update: new Map(),
  },
  version: {
    add: [],
    remove: [],
    update: new Map(),
  },
  proposal: {
    add: new Map(),
    remove: new Map(),
    update: new Map(),
  },
  action: {
    add: new Map(),
    remove: new Map(),
    update: new Map(),
  },
});

/** True when the {@link BatchChangeMap} contains at least one change to apply. */
export const hasAnyBatchChanges = (changes: BatchChangeMap): boolean =>
  changes.element.add.length > 0 ||
  changes.element.remove.length > 0 ||
  changes.element.update.size > 0 ||
  changes.version.add.length > 0 ||
  changes.version.remove.length > 0 ||
  changes.version.update.size > 0 ||
  changes.proposal.add.size > 0 ||
  changes.proposal.remove.size > 0 ||
  changes.proposal.update.size > 0 ||
  changes.action.add.size > 0 ||
  changes.action.remove.size > 0 ||
  changes.action.update.size > 0;

/**
 * Set of all version ids that have at least one proposal- or action-scope mutator
 * across any kind. Used by the apply pipeline to drive the per-version chain.
 */
const collectMutatorVersionIds = (changes: BatchChangeMap): Set<string> => {
  const ids = new Set<string>();
  for (const bucket of [changes.proposal, changes.action]) {
    for (const map of [bucket.add, bucket.remove, bucket.update]) {
      for (const id of map.keys()) ids.add(id);
    }
  }
  return ids;
};

/**
 * Flatten all `(proposal|action) × (remove|add|update)` mutators for one version into
 * the application order documented on {@link applyBatchChanges}:
 * proposal.remove → action.remove → proposal.add → action.add → proposal.update → action.update.
 */
const collectVersionMutators = (
  changes: BatchChangeMap,
  versionId: string,
): VersionMutateChange[] => {
  const ordered: VersionMutateChange[] = [];
  const push = (map: Map<string, VersionMutateChange[]>) => {
    const list = map.get(versionId);
    if (list?.length) ordered.push(...list);
  };
  push(changes.proposal.remove);
  push(changes.action.remove);
  push(changes.proposal.add);
  push(changes.action.add);
  push(changes.proposal.update);
  push(changes.action.update);
  return ordered;
};

/**
 * Index mapping related to a root element (typically a building version)
 */
export type RelativePathIndexMap = Map<IElementID, number[]>;

/**
 * Build an index of element ID -> path for O(1) lookups
 * This allows navigating directly to any element without searching.
 * Is also immune to to reference updates in the project tree (not move/add/remove actions though)
 *
 * @param project - The project to index
 * @returns Map from element ID to its path in the tree
 */
export const buildElementProjectPathMap = (
  project: Project,
): ElementProjectPathMap => {
  profiler.count('buildElementProjectPathMap:calls');
  const index: ElementProjectPathMap = new Map();

  // Post-flatten the project owns baselines directly; buildingIndex stays 0.
  for (let vi = 0; vi < project.baselines.length; vi++) {
    const version = project.baselines[vi]!;
    const versionPathIndex = buildElementPathIndex(version);

    for (const [id, elementIndices] of versionPathIndex) {
      index.set(id, {
        buildingIndex: 0,
        versionIndex: vi,
        elementIndices,
      });
    }
  }

  return index;
};

/**
 * Build a path index for a single root not (typically but not necessarily a version).
 * This is more efficient when working with one root element (e.g. a version) than entire project.
 * I multiple call with the same root element is done it will return a cached index.
 * @param root - The root element to index (typically a version, but can be any element)
 * @returns Map from element ID to element indices path
 */
export const buildElementPathIndex = cacheByArguments(
  (root: OneOfParentElements): RelativePathIndexMap => {
    profiler.count('buildElementPathIndex:calls');
    const index: RelativePathIndexMap = new Map();

    // Index the version itself (empty path)
    index.set(root.id, []);

    const indexElements = (
      elements: OneOfElements[] | undefined,
      parentPath: number[],
    ) => {
      if (!elements) return;

      for (let i = 0; i < elements.length; i++) {
        const element = elements[i]!;
        const currentPath = [...parentPath, i];

        index.set(element.id, currentPath);
        const children = getChildElements(element);

        // Index children recursively
        if (children.length) {
          indexElements(children, currentPath);
        }
      }
    };

    indexElements(root.elements, []);

    return index;
  },
);

/**
 * Get element by path in a parent (version or element). Used inside Immer draft or read-only.
 * @param parent - The root node (version or element, can be a draft)
 * @param path - Element indices to navigate
 * @returns The element at the path, or the parent itself when path is empty
 */
export const getElementByPath = <T extends OneOfParentElements>(
  parent: T,
  path: number[],
): OneOfElements | undefined => {
  // Empty path: parent is always an element-list node (version or element).
  if (path.length === 0) return parent as OneOfElements;

  const elements =
    'elements' in parent ? (parent.elements as OneOfElements[]) : undefined;
  let current: OneOfElements | undefined = elements?.[path[0]!];

  for (let i = 1; i < path.length && current; i++) {
    if (projectGuards.is.element(current) && current.elements) {
      current = current.elements[path[i]!];
    } else {
      return undefined;
    }
  }

  return current;
};

/**
 * Build an element path (version + elements) from a version and index path.
 * Used for dependency resolution so both current and original tree elements can be resolved by id.
 *
 * @param version - The building version
 * @param indices - Element indices path (from {@link buildElementPathIndex})
 * @returns Path [version, e0, e1, ...] to the element at indices, or [version] if indices are empty
 */
export const getElementPathFromIndices = (
  version: Baseline,
  indices: number[],
): ElementPath => {
  const path: ElementPath = [version];
  for (let i = 0; i < indices.length; i++) {
    const el = getElementByPath(version, indices.slice(0, i + 1));
    if (!el || !projectGuards.is.element(el)) break;
    path.push(el);
  }
  return path;
};

/**
 * Applies a resolved element partial onto an Immer draft node.
 * Clones the patch first so frozen nodes from proposal payloads (e.g.
 * {@link ProjectChangeType.ElementCategoryEnrich} `addChildren`) are not written
 * into the tree; later changes on the same child (e.g. `productOverrides`) would
 * otherwise throw "Cannot assign to read only property".
 */
const applyElementPartialToDraft = (
  element: OneOfElements,
  change: Partial<OneOfElements>,
): void => {
  Object.assign(element, cloneDeep(change));
};

/**
 * Apply multiple changes to a parent element (version or element) in a single traversal.
 * Uses Immer for structural sharing.
 *
 * @param parent - The root node (version or element) to update
 * @param changes - Map of element ID to changes
 * @param excludeChildElementChanges - If true (default) we'll exclude child element changes from parent changes to avoid accidental overwrite of children
 * @returns Updated parent (same reference if no changes)
 */
export const applyBatchChangesToParent = <T extends OneOfParentElements>(
  parent: T,
  changes: ElementChangeMap,
  excludeChildElementChanges = true,
): T => {
  profiler.count('applyBatchChangesToParent:calls');
  profiler.countAdd('applyBatchChangesToParent:changes', changes.size);
  if (changes.size === 0) return parent;

  const pathIndex = buildElementPathIndex(parent);

  const excludeFn: (change: ElementPartial) => ElementPartial =
    excludeChildElementChanges
      ? (change: ElementPartial) =>
          'elements' in change ? omit(change, 'elements') : change
      : (change) => change;

  // Apply changes by path length ascending so parent changes run before children;
  // child changes then win and are not overwritten by a parent change that spreads old data
  const entriesByPathLength = Array.from(changes.entries())
    .map(([id, change]) => ({
      id,
      change: excludeFn(change),
      path: pathIndex.get(id),
    }))
    .filter(
      (entry): entry is typeof entry & { path: number[] } =>
        entry.path !== undefined,
    )
    .sort((a, b) => a.path.length - b.path.length);

  return produce(parent, (draft) => {
    // A partial that replaces `elements` (e.g. ElementCategoryEnrich) makes pre-batch
    // paths below it stale: children may be reordered, re-added or gone. Descendants
    // are re-indexed by id once per rewrite; parents apply first (path length order).
    const rewrittenPaths: number[][] = [];
    const relocated = new Map<string, number[]>();
    for (const { id, change, path } of entriesByPathLength) {
      const currentPath =
        relocated.get(id) ??
        (rewrittenPaths.some((prefix) => isStrictPathPrefix(prefix, path))
          ? undefined
          : path);
      if (!currentPath) continue;
      const element = getElementByPath(draft as T, currentPath);
      // A nested rewrite can leave a removed child's old path pointing at a sibling.
      if (
        !element ||
        element.id !== id ||
        !hasPartialChanges(element, change)
      ) {
        continue;
      }
      applyElementPartialToDraft(element, change);
      if (!('elements' in change)) continue;
      rewrittenPaths.push(path);
      // Drop paths recorded by an earlier ancestor rewrite; they belong to children
      // this replacement may have removed or moved.
      forgetRelocatedDescendants(relocated, currentPath);
      indexDescendantPaths(element, currentPath, relocated);
    }
  });
};

/**
 * Remove relocation entries for nodes under {@link prefix}. The rewritten element
 * itself stays, so a later patch can still find it.
 *
 * @param relocated - Id to current path, recorded by earlier rewrites in this batch
 * @param prefix - Path of the element whose children were just replaced
 */
const forgetRelocatedDescendants = (
  relocated: Map<string, number[]>,
  prefix: number[],
): void => {
  for (const [descendantId, descendantPath] of relocated) {
    if (!isStrictPathPrefix(prefix, descendantPath)) continue;
    relocated.delete(descendantId);
  }
};

const isStrictPathPrefix = (prefix: number[], path: number[]): boolean =>
  prefix.length < path.length &&
  prefix.every((index, depth) => path[depth] === index);

/**
 * Record the current index path of every descendant of {@link root}.
 *
 * @param root - Element whose subtree is indexed (may be an Immer draft)
 * @param rootPath - Current path of {@link root} in the batch root
 * @param into - Map from element id to path, overwritten per id
 */
const indexDescendantPaths = (
  root: OneOfElements,
  rootPath: number[],
  into: Map<string, number[]>,
): void => {
  getChildElements(root).forEach((child, index) => {
    const childPath = [...rootPath, index];
    into.set(child.id, childPath);
    indexDescendantPaths(child, childPath, into);
  });
};

/** @deprecated Use {@link applyBatchChangesToParent} instead */
export const applyBatchChangesToVersion = applyBatchChangesToParent;

/**
 * Apply pre-resolved update partials to a project per-version.
 *
 * Workhorse for the update phase of {@link applyBatchChanges}; structural changes are
 * not handled here. Useful directly when a caller has already computed its element
 * patches and just wants them applied with structural sharing.
 *
 * @param project - The project to update
 * @param changes - Map of element/version id to merged element partials
 * @param excludeChildElementChanges - When true, partials won't overwrite element.elements.
 *   Default `false` matches the project-level pipeline (some update handlers, e.g.
 *   category enrichment, intentionally rewrite children).
 * @returns Updated project (same reference if no changes)
 */
export const applyUpdatePartialsToProject = (
  project: Project,
  changes: ElementChangeMap,
  excludeChildElementChanges = false,
): Project => {
  if (changes.size === 0) return project;

  const versions = getAllBuildingVersions(project);
  const patches = versions
    .map((version) => {
      const updated = applyBatchChangesToParent(
        version,
        changes,
        excludeChildElementChanges,
      );
      // Return undefined if the version is not changed to avoid unnecessary updates
      return updated !== version ? updated : undefined;
    })
    .filter((v): v is Baseline => v !== undefined);

  if (patches.length === 0) return project;

  return updateBuildingVersionProperties(project, ...patches);
};

/**
 * Removes queued work that still targets element ids removed in the same batch: `element.update`
 * keys for the subtree, and proposal/action mutator queues (including nested
 * {@link ProjectChangeType.ProposalAppendAction} bodies) for the matching `versionId`.
 */
export const pruneBatchChangeMapAfterElementRemovals = (
  changes: BatchChangeMap,
  removedIdsByVersion: ReadonlyMap<string, Set<string>>,
): void => {
  if (removedIdsByVersion.size === 0) return;

  const allRemoved = new Set<string>();
  for (const ids of removedIdsByVersion.values()) {
    for (const id of ids) {
      allRemoved.add(id);
    }
  }
  for (const id of allRemoved) {
    changes.element.update.delete(id);
  }

  const pruneScopedMutators = (bucket: ScopedMutatorBucket): void => {
    for (const map of [bucket.add, bucket.remove, bucket.update] as const) {
      for (const [versionId, queue] of map) {
        const removed = removedIdsByVersion.get(versionId);
        if (!removed?.size || queue.length === 0) continue;
        const next = stripProjectChangesForRemovedElements(removed, queue);
        if (
          next.length !== queue.length ||
          next.some((c, i) => c !== queue[i])
        ) {
          map.set(versionId, next);
        }
      }
    }
  };

  pruneScopedMutators(changes.proposal);
  pruneScopedMutators(changes.action);
};

/** Ids dropped by the version removals of one batch, see {@link collectRemovedVersionIds}. */
type RemovedVersionIds = {
  /** The removed building versions themselves */
  versionIds: string[];
  /** Those versions plus every descendant element — everything gone from the tree */
  allIds: ReadonlySet<string>;
};

/**
 * Removes every queued change that targets a building version removed in the same batch:
 * structural element ops into its subtree, element partials, its own version-scope updates
 * and its whole proposal/action queues (they die with the version).
 *
 * Re-adding the same version in this batch is supported — {@link applyBatchChanges} applies
 * removes then adds, so the id comes back. `version.add` is left alone, and
 * {@link ProjectChangeType.ElementAdd} whose parent is a re-added version id is kept so a
 * child can be inserted in the same batch.
 * Ops against old descendant ids, and version/proposal/action updates for the removed id,
 * are still dropped: those nodes died with the previous tree.
 *
 * @param changes - In-flight {@link BatchChangeMap}, mutated in place
 * @param removed - Ids collected on the *before* state by {@link collectRemovedVersionIds}
 */
const pruneBatchChangeMapAfterVersionRemovals = (
  changes: BatchChangeMap,
  { versionIds, allIds }: RemovedVersionIds,
): void => {
  if (versionIds.length === 0) return;

  // ElementAdd.elementId is the parent; keep adds into a version this batch puts back
  const reAddedVersionIds = new Set(
    changes.version.add
      .map((change) => change.versionId)
      .filter((id) => versionIds.includes(id)),
  );

  changes.element.add = changes.element.add.filter(
    (change) =>
      !allIds.has(change.elementId) || reAddedVersionIds.has(change.elementId),
  );
  changes.element.remove = changes.element.remove.filter(
    (change) => !allIds.has(change.elementId),
  );
  for (const id of allIds) {
    changes.element.update.delete(id);
  }

  for (const versionId of versionIds) {
    changes.version.update.delete(versionId);
    for (const bucket of [changes.proposal, changes.action]) {
      bucket.add.delete(versionId);
      bucket.remove.delete(versionId);
      bucket.update.delete(versionId);
    }
  }
};

/**
 * Collect every id the version removals of a batch drop. Has to run on the *before* state
 * since the elements are gone once the removal is applied. Unknown version ids are skipped —
 * a replayed batch may target a version that is already gone.
 */
const collectRemovedVersionIds = (
  beforeProject: Project,
  changes: ReadonlyArray<VersionRemoveChange>,
): RemovedVersionIds => {
  const versionIds: string[] = [];
  const allIds = new Set<string>();
  for (const { versionId } of changes) {
    const version = getVersionById(beforeProject, versionId, false);
    if (!version) continue;
    versionIds.push(versionId);
    for (const { id } of flattenElements(version)) allIds.add(id);
  }
  return { versionIds, allIds };
};

/** Keeps a bulk remove from pushing an unbounded id array into the error report. */
const MAX_REPORTED_ELEMENT_IDS = 20;

const toReportedElementIds = (
  elementIds: readonly string[],
): { elementIds: string[]; elementIdCount: number } => ({
  elementIds: elementIds.slice(0, MAX_REPORTED_ELEMENT_IDS),
  elementIdCount: elementIds.length,
});

/**
 * Report element removes whose targets were not in the tree. The batch still continues
 * so a stale id or a proposal-only recipe child cannot abort a save.
 *
 * Reported as two separate errors so each groups on its own in Sentry:
 * - A recipe-scoped id is the known proposal-only child limitation (DEV-3656). `ElementRemove` is
 *   in `PROJECT_CHANGE_TYPES_EXCLUDED_FROM_PROPOSAL`, so deleting a row that exists only in the
 *   proposal-resolved tree routes the remove to baseline, where it has nothing to hit. Expected
 *   on ordinary use, and its volume tracks how often users delete recipe children in a proposal.
 * - Anything else is unexplained — a stale or double remove — and is the one worth chasing.
 *
 * Reaches Sentry via {@link reportSharedError}; the user is not notified.
 */
const reportSkippedMissingElementRemoves = (
  requestedIds: string[],
  appliedIds: string[],
): void => {
  const applied = new Set(appliedIds);
  const missingIds = [
    ...new Set(requestedIds.filter((id) => !applied.has(id))),
  ];
  if (missingIds.length === 0) return;

  const proposalOnlyIds = missingIds.filter(isRecipeScopedElementId);
  if (proposalOnlyIds.length > 0) {
    reportSharedError(
      new Error('Skipped element remove for proposal-only recipe child'),
      toReportedElementIds(proposalOnlyIds),
    );
  }

  const unexplainedIds = missingIds.filter(
    (id) => !isRecipeScopedElementId(id),
  );
  if (unexplainedIds.length === 0) return;

  reportSharedError(
    new Error('Skipped element remove for missing target'),
    toReportedElementIds(unexplainedIds),
  );
};

/**
 * Options for {@link applyBatchChanges}.
 *
 * `replayOfRace` is the rebase of a change set onto server truth after an
 * optimistic-lock 422. An element remove whose target is already absent was
 * applied by the write that won the lock, or removed upstream, and is a no-op.
 * That miss is not reported. A direct edit still reports it.
 */
export type ApplyBatchChangesOptions = {
  replayOfRace?: boolean;
};

/**
 * Apply structural and update changes to a project in one go.
 *
 * Application order across {@link BatchChangeMap} buckets is fixed so structural changes
 * always run before partials and proposal/action mutators see freshly added elements. It is
 * a bucket order, not the caller's array order: removes always precede adds of the same
 * scope, so removing and re-adding the same id in one batch leaves the id in place.
 *
 * 1. `version.remove` — {@link applyVersionRemoveChange} per change, first of all: the
 *    in-flight {@link BatchChangeMap} is then pruned so no later step operates on a version
 *    that no longer exists.
 * 2. `element.remove` — {@link removeElements} followed by
 *    {@link cleanupActionsForRemovedElementsInProject} so actions referencing dropped
 *    descendants are scrubbed; then the in-flight {@link BatchChangeMap} is pruned so
 *    later steps never re-queue updates or append nested edits against removed ids.
 * 3. `version.add` — {@link applyVersionAddChange} per change, before element adds so a
 *    version and children for it can be added in the same batch.
 * 4. `element.add` — {@link applyElementAddChange} per change, sequentially, so each insert
 *    sees the previous one (matters for sibling ordering, name dedup and proposal-selection
 *    remap inside `addElements`).
 * 5. Per-version structural mutators in fixed sub-order
 *    (proposal.remove → action.remove → proposal.add → action.add → proposal.update →
 *    action.update). Each mutator returns a full updated {@link Baseline} reference.
 * 6. `version.update` — primitive partials (`VersionName/Products/Results`) and bulk resets
 *    (`VersionClearProposals`) per version, in insertion order.
 * 7. `element.update` — raw changes resolved into element partials against the
 *    post-structural project, then applied per version; then
 *    {@link dropRedundantChangesFromStoredProposalActionsInProject} strips v2 proposal
 *    {@link Action} changes that already match the new baseline (same scope and value).
 *
 * @param project - The project to update
 * @param changes - {@link BatchChangeMap} produced by `buildElementChangesMap`
 * @param options - {@link ApplyBatchChangesOptions}. `replayOfRace` skips the
 *   missing-remove report; the remove itself is still a no-op.
 * @returns Updated project (same reference if no changes)
 */
export const applyBatchChanges = (
  project: Project,
  changes: BatchChangeMap,
  options?: ApplyBatchChangesOptions,
): Project => {
  if (!hasAnyBatchChanges(changes)) return project;

  let next = project;

  // 1. Version removes (drop queued work for the version before anything else touches it)
  if (changes.version.remove.length > 0) {
    const removed = collectRemovedVersionIds(next, changes.version.remove);
    next = changes.version.remove.reduce(applyVersionRemoveChange, next);
    pruneBatchChangeMapAfterVersionRemovals(changes, removed);
  }

  // 2. Element removes (capture descendant ids BEFORE removal so action cleanup can find them)
  if (changes.element.remove.length > 0) {
    // Skip missing targets instead of aborting: a proposal-only recipe child has no
    // baseline row, and a stale/double remove is also safe to ignore. Report both to Sentry
    // so the skip is not silent; do not throw (that would fail the whole save).
    // A 422 replay is the exception: the target is already gone on server truth.
    const requestedIds = changes.element.remove.map((c) => c.elementId);
    const removalIds = requestedIds.filter((id) =>
      Boolean(getElementById(next, id, false)),
    );
    if (!options?.replayOfRace) {
      reportSkippedMissingElementRemoves(requestedIds, removalIds);
    }

    if (removalIds.length > 0) {
      const removedIdsByVersion = collectRemovedElementIdsByVersion(
        next,
        removalIds,
      );
      next = cleanupActionsForRemovedElementsInProject(
        removeElements(next, ...removalIds),
        removedIdsByVersion,
      );
      pruneBatchChangeMapAfterElementRemovals(changes, removedIdsByVersion);
    }
  }

  // 3. Version adds (before element adds so a new version can receive children in one batch)
  if (changes.version.add.length > 0) {
    next = changes.version.add.reduce(applyVersionAddChange, next);
  }

  // 4. Element adds (sequential)
  if (changes.element.add.length > 0) {
    next = changes.element.add.reduce(applyElementAddChange, next);
  }

  // 5. Per-version proposal/action mutator chains
  next = applyVersionMutators(next, changes);

  // 6. Version-scope updates (mix of partial + structural reset)
  next = applyVersionUpdates(next, changes.version.update);

  // 7. Element-scope update partials
  if (changes.element.update.size === 0) return next;

  const partials = resolveElementUpdatePartials(next, changes.element.update);
  if (partials.size === 0) return next;

  next = applyUpdatePartialsToProject(next, partials, false);
  return dropRedundantChangesFromStoredProposalActionsInProject(next);
};

/**
 * Run the per-version structural mutator chain (proposals + actions) against the project.
 * Each version chain is folded through {@link applyVersionMutateChange} in the order
 * documented on {@link applyBatchChanges}, so subsequent mutators in the same version
 * observe earlier results.
 *
 * Versions without mutators are skipped to preserve structural sharing.
 */
const applyVersionMutators = (
  project: Project,
  changes: BatchChangeMap,
): Project => {
  const versionIds = collectMutatorVersionIds(changes);
  if (versionIds.size === 0) return project;

  const versions = getAllBuildingVersions(project);
  const patches: Baseline[] = [];

  for (const version of versions) {
    if (!versionIds.has(version.id)) continue;
    const ordered = collectVersionMutators(changes, version.id);
    if (!ordered.length) continue;

    let nextVersion = version;
    for (const change of ordered) {
      nextVersion = applyVersionMutateChange(nextVersion, change);
    }
    if (nextVersion !== version) patches.push(nextVersion);
  }

  if (patches.length === 0) return project;
  return updateBuildingVersionProperties(project, ...patches);
};

/**
 * Apply version-scope updates (mix of primitive partials and structural resets like
 * `VersionClearProposals`). For each version we walk changes in insertion order,
 * routing structural changes through {@link applyVersionMutateChange} and primitive
 * changes through {@link versionChangeToPartial} so a single batch can mix both.
 */
const applyVersionUpdates = (
  project: Project,
  versionUpdates: Map<string, ProjectChange[]>,
): Project => {
  if (versionUpdates.size === 0) return project;

  const versions = getAllBuildingVersions(project);
  const patches: Baseline[] = [];

  for (const version of versions) {
    const changes = versionUpdates.get(version.id);
    if (!changes?.length) continue;

    let nextVersion: Baseline = version;
    for (const change of changes) {
      if (isVersionMutateChangeType(change.type)) {
        nextVersion = applyVersionMutateChange(
          nextVersion,
          change as VersionMutateChange,
        );
        continue;
      }
      if (!isVersionChange(change)) continue;
      const partial = versionChangeToPartial(nextVersion, change);
      if (partial) {
        nextVersion = { ...nextVersion, ...partial } as Baseline;
      }
    }

    if (nextVersion !== version) patches.push(nextVersion);
  }

  if (patches.length === 0) return project;
  return updateBuildingVersionProperties(project, ...patches);
};

const versionMutateChangeTypeSet = new Set<ProjectChangeType>(
  versionMutateChangeTypes,
);

const isVersionMutateChangeType = (type: ProjectChangeType): boolean =>
  versionMutateChangeTypeSet.has(type);

/** Type guard for version-scoped {@link ProjectChange}s. */
const isVersionChange = (change: ProjectChange): change is VersionChange =>
  isOneOf(versionChangeTypes, change.type);

/**
 * Resolve raw element-scope update changes (grouped by element id) into a single merged
 * partial per id. Walks each id's changes in order, threading the merged element snapshot
 * so subsequent handlers in the same batch see prior partials applied.
 *
 * Version-scope updates do NOT pass through here — they're handled by
 * {@link applyVersionUpdates} which can mix primitive partials with structural mutators
 * (e.g. `VersionClearProposals`).
 */
const RECIPE_FALLBACK_NAME_CHANGE_TYPES: ReadonlySet<ProjectChangeType> =
  new Set([
    ProjectChangeType.ElementRecipeApply,
    ProjectChangeType.ElementRecipeDetach,
    ProjectChangeType.ElementRecipeClear,
  ]);

/**
 * Minimal recipe lookup from a batch so {@link generateElementFallbackName} can use
 * {@link ProjectChangeType.ElementRecipeApply} payload names during proposal replay.
 */
const buildRecipeLookupFromChanges = (
  changes: ProjectChange[],
): RecipeRecord | undefined => {
  const apply = changes.find(
    (
      change,
    ): change is ProjectChangeMap[ProjectChangeType.ElementRecipeApply] =>
      change.type === ProjectChangeType.ElementRecipeApply,
  );
  if (!apply) return undefined;
  return { [apply.recipeId]: apply.recipe };
};

/**
 * Refreshes {@link IElement.fallbackName} after recipe apply/detach/clear using version
 * sibling context (same rules as the client {@link useApplyFallbackName} preview).
 */
const augmentPartialWithRecipeFallbackName = (
  version: Baseline,
  element: IElement,
  merged: Partial<IElement>,
  changes: ProjectChange[],
): Partial<IElement> => {
  if (
    !changes.some((change) =>
      RECIPE_FALLBACK_NAME_CHANGE_TYPES.has(change.type),
    )
  ) {
    return merged;
  }

  const mergedElement = { ...element, ...merged };
  const fallbackName = generateElementFallbackName(
    version,
    mergedElement,
    buildRecipeLookupFromChanges(changes),
  );
  if (getFallbackName(mergedElement) === fallbackName) return merged;
  return { ...merged, fallbackName };
};

const resolveElementUpdatePartials = (
  project: Project,
  updateChanges: Map<string, ProjectChange[]>,
): ElementChangeMap => {
  const pathIndex = buildElementProjectPathMap(project);
  const partials: ElementChangeMap = new Map();

  for (const [id, changes] of updateChanges) {
    const path = pathIndex.get(id);
    if (!path) continue;

    const version = project.baselines[path.versionIndex];
    if (!version) continue;

    const element = getElementByPath(version, path.elementIndices);
    if (!element) continue;

    let merged: Partial<OneOfElements> = {};
    let currentElement: OneOfElements = element;
    for (const change of changes) {
      const partial = projectChangeToElementPartial(currentElement, change);
      if (partial) {
        merged = mergeElementPartials(merged, partial);
        currentElement = { ...currentElement, ...partial } as OneOfElements;
      }
    }

    if (isElement(element)) {
      merged = augmentPartialWithRecipeFallbackName(
        version,
        element,
        merged as Partial<IElement>,
        changes,
      );
    }

    // Compare merged patch to the element snapshot before the loop (not redundant with per-change checks).
    if (Object.keys(merged).length > 0 && hasPartialChanges(element, merged)) {
      partials.set(id, merged);
    }
  }

  return partials;
};

/**
 * Group changes by version for more efficient batch processing
 * @param changes - Map of element ID to changes
 * @param pathIndex - Pre-built path index
 * @returns Map of version ID to changes for that version
 */
export const groupChangesByVersion = (
  changes: Map<string, Partial<OneOfElements>>,
  pathIndex: ElementProjectPathMap,
): Map<string, Map<string, Partial<OneOfElements>>> => {
  const grouped = new Map<string, Map<string, Partial<OneOfElements>>>();

  for (const [id, change] of changes) {
    const elementPath = pathIndex.get(id);
    if (!elementPath) continue;

    // Create a version key from building and version indices
    const versionKey = `${elementPath.buildingIndex}:${elementPath.versionIndex}`;

    let versionChanges = grouped.get(versionKey);
    if (!versionChanges) {
      versionChanges = new Map();
      grouped.set(versionKey, versionChanges);
    }
    versionChanges.set(id, change);
  }

  return grouped;
};

/**
 * Get the path to an element by ID using a pre-built index
 *
 * @param pathIndex - Pre-built path index
 * @param elementId - Element ID to look up
 * @returns Path to the element, or undefined if not found
 */
export const getPathById = (
  pathIndex: ElementProjectPathMap,
  elementId: string,
): ElementProjectPath | undefined => {
  return pathIndex.get(elementId);
};
